import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Book, Code, Zap, HelpCircle } from 'lucide-react';
import { LandingHeader } from '../components/layout/landing-header';

const DocsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <LandingHeader currentPage="docs" />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Documentation
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to get started with PageMind
          </p>
        </div>

        {/* Coming Soon Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 mb-12 max-w-3xl mx-auto">
          <h3 className="text-xl font-semibold text-blue-900 mb-2">
            Full Documentation Coming Soon!
          </h3>
          <p className="text-blue-800 mb-4">
            We're working on comprehensive documentation to help you make the most of PageMind. 
            In the meantime, here are some quick resources to get you started.
          </p>
          <Button variant="outline" onClick={() => navigate('/contact')}>
            Contact Support
          </Button>
        </div>

        {/* Quick Start Guide */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <Book className="h-8 w-8 text-blue-600 mb-4" />
            <h3 className="font-semibold mb-2">Getting Started</h3>
            <p className="text-sm text-gray-600 mb-4">
              Learn how to add PageMind to your website in minutes.
            </p>
            <Button variant="link" className="p-0 h-auto">
              Coming Soon →
            </Button>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <Code className="h-8 w-8 text-blue-600 mb-4" />
            <h3 className="font-semibold mb-2">API Reference</h3>
            <p className="text-sm text-gray-600 mb-4">
              Integrate PageMind with your applications using our API.
            </p>
            <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/docs/feed-export')}>
              Feed Export API →
            </Button>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <Zap className="h-8 w-8 text-blue-600 mb-4" />
            <h3 className="font-semibold mb-2">Best Practices</h3>
            <p className="text-sm text-gray-600 mb-4">
              Tips for optimizing your chatbot's performance.
            </p>
            <Button variant="link" className="p-0 h-auto">
              Coming Soon →
            </Button>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <HelpCircle className="h-8 w-8 text-blue-600 mb-4" />
            <h3 className="font-semibold mb-2">FAQs</h3>
            <p className="text-sm text-gray-600 mb-4">
              Answers to commonly asked questions.
            </p>
            <Button variant="link" className="p-0 h-auto">
              Coming Soon →
            </Button>
          </div>
        </div>

        {/* Quick Integration Guide */}
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold mb-6">Quick Integration Guide</h3>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h4 className="font-semibold mb-4">1. Get Your Site ID</h4>
            <p className="text-gray-600 mb-4">
              After creating your account and adding your website, you'll receive a unique Site ID from your dashboard.
            </p>
            
            <h4 className="font-semibold mb-4">2. Add the PageMind Script</h4>
            <p className="text-gray-600 mb-2">
              Add this code before the closing <code className="bg-gray-100 px-1 rounded">&lt;/body&gt;</code> tag on your website:
            </p>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`<script src="https://pixel.pagemind.ai/pagemind.js"></script>
<script>
  PageMind.init({
    siteId: "YOUR_SITE_ID"
  });
</script>`}
            </pre>
            
            <h4 className="font-semibold mb-4 mt-6">3. That's It!</h4>
            <p className="text-gray-600">
              PageMind will automatically start learning from your website content and be ready to help your visitors.
            </p>
          </div>
        </div>

        {/* Need Help */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold mb-4">Need Help?</h3>
          <p className="text-gray-600 mb-6">
            Our support team is here to assist you with any questions or issues.
          </p>
          <div className="flex justify-center space-x-4">
            <Button onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
            <Button variant="outline" onClick={() => window.open('mailto:support@pagemind.ai')}>
              Email Us
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocsPage;